import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { setAuthToken } from '@/api/client';
import { TaskProgressProvider } from '@/api/TaskProgressProvider';
import { config } from '@/config/app';
import { i18n } from '@/i18n';
import { AppHeader } from '@/layout/AppHeader';
import { PageProvider, usePageContext } from '@/layout/PageContext';
import { ProjectDetail } from '@/routes/ProjectDetail';
import { ProjectList } from '@/routes/ProjectList';
import '@/styles/global.css';
import { useEffect } from 'react';

const queryClient = new QueryClient({});

const oidcConfig = {
  authority: config.authority,
  // biome-ignore lint/style/useNamingConvention: OIDC spec requires snake_case property names
  client_id: config.clientId,
  // biome-ignore lint/style/useNamingConvention: OIDC spec requires snake_case property names
  redirect_uri: window.location.origin,
};

interface AppShellProps {
  onLogout: () => void;
}

/**
 * Layout shell that wires the router location to AppHeader breadcrumb.
 */
const AppShell = ({ onLogout }: AppShellProps): React.ReactElement => {
  const location = useLocation();
  const { projectName } = usePageContext();

  const isDetail = location.pathname.startsWith('/projects/') && location.pathname.length > '/projects/'.length;

  return (
    <>
      <AppHeader currentPage={isDetail ? 'detail' : 'list'} projectName={projectName} onLogout={onLogout} />
      <main>
        <Routes>
          <Route path="/projects" element={<ProjectList />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="*" element={<Navigate to="/projects" replace={true} />} />
        </Routes>
      </main>
    </>
  );
};

/**
 * Inner app that requires authentication.
 * Sets the API auth token from the OIDC user session.
 */
const AuthenticatedApp = (): React.ReactElement => {
  const auth = useAuth();

  // Set synchronously so the token is present before any child effect fires a request.
  // React runs effects bottom-up (children before parents), so a useEffect-only approach
  // would leave children without a token on the first render after page load.
  setAuthToken(auth.user?.access_token ?? null);

  // Also sync via effect to propagate silent token renewals that may not re-render this
  // component (e.g. if react-oidc-context ever updates the user object without a context
  // notification).
  useEffect(() => {
    setAuthToken(auth.user?.access_token ?? null);
  }, [auth.user?.access_token]);

  if (auth.isLoading) {
    return <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>;
  }

  if (!auth.isAuthenticated) {
    auth.signinRedirect({
      // biome-ignore lint/style/useNamingConvention: OIDC spec requires snake_case property names
      redirect_uri: window.location.href,
    });
    return <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>;
  }

  const handleLogout = (): void => {
    setAuthToken(null);
    auth.signoutRedirect();
  };

  return <AppShell onLogout={handleLogout} />;
};

/**
 * Root application component.
 * Wraps the app in OIDC, router and language context providers.
 */
const App = (): React.ReactElement => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider {...oidcConfig}>
        <Router>
          <I18nProvider i18n={i18n}>
            <PageProvider>
              <TaskProgressProvider>
                <AuthenticatedApp />
              </TaskProgressProvider>
            </PageProvider>
            <Toaster position="bottom-right" duration={4000} />
          </I18nProvider>
        </Router>
      </AuthProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
};

export default App;
